import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useStore } from "../context/StoreContext";
import { formatEUR, COUNTRIES, zoneOf } from "../lib/api";
import Placeholder from "./Placeholder";
import { useEffect, useState } from "react";

const CartDrawer = () => {
  const { cart, drawerOpen, setDrawerOpen, updateQty, removeFromCart, subtotal, settings } = useStore();
  const navigate = useNavigate();
  const [zone, setZone] = useState("france");

  useEffect(() => {
    const saved = localStorage.getItem("moha66_country");
    if (saved) setZone(zoneOf(saved));
  }, []);

  const fee = settings
    ? (settings.shipping?.free_threshold && subtotal >= settings.shipping.free_threshold
        ? 0
        : settings.shipping?.[zone] ?? 0)
    : null;

  return (
    <>
      {drawerOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
          data-testid="cart-drawer-overlay"
        />
      )}
      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: drawerOpen ? 0 : "100%" }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 right-0 z-50 h-full w-full sm:w-[440px] bg-coal border-l border-line flex flex-col"
        data-testid="cart-drawer"
        aria-hidden={!drawerOpen}
      >
        <div className="flex items-center justify-between px-6 h-16 border-b border-line">
          <h2 className="font-display font-bold uppercase tracking-widest text-lg">Panier ({cart.length})</h2>
          <button data-testid="cart-close-button" onClick={() => setDrawerOpen(false)} className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-white transition-colors">
            Fermer ✕
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 text-center">
            <span className="font-display font-extrabold uppercase text-outline text-6xl">Vide</span>
            <p className="text-sm text-neutral-400">Votre panier attend sa première pièce Moha66.</p>
            <button
              data-testid="cart-empty-shop-button"
              onClick={() => { setDrawerOpen(false); navigate("/boutique"); }}
              className="bg-white text-ink font-mono text-xs uppercase tracking-[0.2em] px-8 py-3.5 hover:bg-neutral-300 transition-colors"
            >
              Découvrir la boutique
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="cart-items-list">
              {cart.map((item, idx) => (
                <div key={`${item.product_id}-${item.size}-${idx}`} className="flex gap-4 py-4 border-b border-line" data-testid="cart-item">
                  <div className="w-20 h-24 shrink-0 border border-line overflow-hidden">
                    {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" /> : <Placeholder label="" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-grotesk font-semibold uppercase text-sm text-white truncate">{item.name}</p>
                    <p className="mt-0.5 font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-500">
                      Taille {item.size}{item.color ? ` · ${item.color}` : ""}{item.custom_text ? ` · N° ${item.custom_text}` : ""}
                    </p>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="flex items-center border border-line">
                        <button data-testid={`cart-qty-minus-${idx}`} onClick={() => updateQty(idx, item.quantity - 1)} disabled={item.quantity <= 1} className="w-7 h-7 text-neutral-400 hover:text-white disabled:opacity-30">−</button>
                        <span data-testid={`cart-qty-value-${idx}`} className="w-8 text-center font-mono text-xs">{item.quantity}</span>
                        <button data-testid={`cart-qty-plus-${idx}`} onClick={() => updateQty(idx, item.quantity + 1)} className="w-7 h-7 text-neutral-400 hover:text-white">+</button>
                      </div>
                      <button data-testid={`cart-remove-${idx}`} onClick={() => removeFromCart(idx)} className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500 hover:text-white underline underline-offset-4">
                        Retirer
                      </button>
                    </div>
                  </div>
                  <p className="font-mono text-sm text-silver">{formatEUR(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-line px-6 py-5 space-y-2">
              <div className="flex justify-between text-sm text-neutral-400">
                <span>Sous-total</span>
                <span data-testid="cart-subtotal" className="text-white font-mono">{formatEUR(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-neutral-400">
                <span>Livraison ({COUNTRIES.find((c) => zoneOf(c.code) === zone)?.name})</span>
                <span data-testid="cart-shipping" className="text-white font-mono">{fee === null ? "—" : fee === 0 ? "Offerte" : formatEUR(fee)}</span>
              </div>
              {settings?.shipping?.free_threshold > 0 && subtotal < settings.shipping.free_threshold && (
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                  Livraison offerte dès {formatEUR(settings.shipping.free_threshold)}
                </p>
              )}
              <button
                data-testid="cart-checkout-button"
                onClick={() => { setDrawerOpen(false); navigate("/checkout"); }}
                className="mt-3 w-full bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] py-4 hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                Passer commande — {formatEUR(subtotal + (fee || 0))}
              </button>
              <p className="text-center font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-600 pt-1">
                Paiement sécurisé Stripe
              </p>
            </div>
          </>
        )}
      </motion.aside>
    </>
  );
};

export default CartDrawer;
